import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import * as translator from "../../../../../utils/translator";
import { MyAccountStoreProduct } from "../types";

interface Props {
  language: string;
  translationPrefix: string;
  product: MyAccountStoreProduct;
  tagDimensions: string[];
  onClose: () => void;
}

const MyAccountStoreTagPopup: React.FC<Props> = ({
  language,
  translationPrefix,
  product,
  tagDimensions,
  onClose,
}) => {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const t = (key: string): string =>
    translator.translate(language, "my_account", `${translationPrefix}/${key}`);

  const sections = tagDimensions
    .map((dimensionKey) => {
      const values =
        product.dimensions.find((d) => d.dimensionKey === dimensionKey)?.values ?? [];
      return { dimensionKey, values };
    })
    .filter((s) => s.values.length > 0);

  const valueLabel = (dimensionKey: string, value: string): string =>
    translator.translate(
      language,
      "my_account",
      `${translationPrefix}/filters/${dimensionKey}/values/${value}`,
    ) || value;

  const content = (
    <div
      className="fixed inset-0 z-[99999] flex items-end lg:items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      <div className="relative z-10 w-full lg:w-[510px] max-h-[70vh] lg:max-h-[80vh] bg-white rounded-t-3xl lg:rounded-[20px] shadow-2xl flex flex-col overflow-hidden">
        {/* Green gradient header */}
        <div
          className="relative px-5 lg:px-[22px] pt-3.5 pb-3 shrink-0"
          style={{ background: "linear-gradient(101deg, #279B6E 1.6%, #54A951 102.8%)" }}
        >
          <button
            type="button"
            className="lg:hidden absolute top-3 right-3 flex items-center justify-center w-8 h-8 rounded-full bg-white/20"
            aria-label={t("popup/close")}
            onClick={onClose}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4l8 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
          <h2 className="m-0 font-semibold text-xl lg:text-2xl leading-tight text-white pr-10 lg:pr-0">
            {product.title}
          </h2>
          <p className="m-0 text-sm text-white/95">{t("popup/subtitle")}</p>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 lg:px-[22px] pt-5 lg:pt-6 pb-4">
          <div className="flex flex-col gap-4 lg:gap-6">
            {sections.map((section) => (
              <div key={section.dimensionKey} className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#5fb574] shrink-0" />
                  <h3 className="m-0 font-semibold text-base lg:text-lg text-[#1a1a1a]">
                    {t(`popup/${section.dimensionKey}`)} ({section.values.length})
                  </h3>
                </div>
                <ul className="m-0 p-0 list-none flex flex-col gap-1.5 lg:gap-2.5">
                  {section.values.map((value) => (
                    <li
                      key={value}
                      className="pl-4 text-sm lg:text-[15px] leading-relaxed text-[#333]"
                    >
                      {valueLabel(section.dimensionKey, value)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Close button */}
        <div className="shrink-0 px-5 lg:px-[22px] pb-5 lg:pb-6 pt-2">
          <button
            type="button"
            className="w-full h-[50px] lg:h-[52px] rounded-xl text-white font-medium text-[15px] lg:text-base"
            style={{ background: "linear-gradient(101deg, #269c6e 1.6%, #7aba47 102.8%)" }}
            onClick={onClose}
          >
            {t("popup/close")}
          </button>
        </div>
      </div>
    </div>
  );

  const modalRoot =
    (typeof document !== "undefined" && document.getElementById("modal-root")) ||
    (typeof document !== "undefined" ? document.body : null);

  return modalRoot ? createPortal(content, modalRoot) : content;
};

export default MyAccountStoreTagPopup;
